import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("error");
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          setPaymentDetails(response.data);
          return;
        } else if (response.data.status === "expired") {
          setStatus("error");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus("error");
        }
      }
    };

    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900 min-h-[70vh] flex items-center">
        <div className="max-w-xl mx-auto px-6 text-center">
          {status === "loading" && (
            <div className="animate-fade-in" data-testid="checkout-loading">
              <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
              <h1 className="font-heading text-3xl font-bold text-white mb-4">
                Processing Your Payment
              </h1>
              <p className="text-slate-300">
                Please wait while we confirm your payment...
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="animate-fade-in" data-testid="checkout-success">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="font-heading text-4xl font-bold text-white mb-4">
                Welcome Aboard!
              </h1>
              <p className="text-lg text-slate-300 mb-8">
                Your payment of <span className="text-primary font-semibold">${(paymentDetails?.amount_total / 100).toFixed(2) || "997.00"}</span> has been confirmed.
                You're now a client of A.I. Means Business.
              </p>

              <div className="glass p-6 rounded-2xl mb-8 text-left">
                <h3 className="font-heading font-semibold text-white mb-4">What happens next?</h3>
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>You'll receive a confirmation email with your login credentials</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Access your Client Portal to view your scheduled appointment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Complete a brief questionnaire before your discovery session</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/login">
                  <Button className="bg-primary hover:bg-primary/90 text-white rounded-full px-8">
                    Go to Login
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="border-slate-700 text-slate-300 hover:text-white rounded-full px-8">
                    Return Home
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="animate-fade-in" data-testid="checkout-error">
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="font-heading text-4xl font-bold text-white mb-4">
                Payment Issue
              </h1>
              <p className="text-lg text-slate-300 mb-8">
                We couldn't confirm your payment. If you believe this is an error, 
                please contact us or try again.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/pricing">
                  <Button className="bg-primary hover:bg-primary/90 text-white rounded-full px-8">
                    Try Again
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-slate-700 text-slate-300 hover:text-white rounded-full px-8">
                    Contact Support
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default CheckoutSuccessPage;
